import { useCallback, useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Handshake, SignOut, ChartLineUp, Buildings, Receipt, Tag, Plus, Copy, Question, FolderOpen, Funnel } from "@phosphor-icons/react";
import { toast } from "sonner";
import API from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import ThemeMenu from "../../components/ThemeMenu";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { money } from "../platform/ResellersPage";
import { MonthsTable, LedgerTable, openPdf } from "../platform/ResellerDetailPage";
import { PartnerPageCard } from "../../components/reseller/PartnerPageCard";
import { TierCard, AlertsCard } from "../../components/reseller/TierCard";
import { HealthBadge, HealthCounts } from "../../components/reseller/HealthBadge";
import { LinkFunnel } from "../../components/reseller/LinkFunnel";
import { ResourcesHub } from "../../components/reseller/ResourcesHub";
import { ProspectPipeline } from "../../components/reseller/ProspectPipeline";

const NAV = [
  { to: "/reseller", label: "Overview", icon: ChartLineUp, end: true },
  { to: "/reseller/prospects", label: "Prospects", icon: Funnel },
  { to: "/reseller/tenants", label: "My tenants", icon: Buildings },
  { to: "/reseller/statements", label: "Statements", icon: Receipt },
  { to: "/reseller/resources", label: "Resources", icon: FolderOpen },
];
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB") : "—");

function Layout({ children }) {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="reseller-portal">
      <header className="border-b border-border bg-card/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center gap-4">
          <Link to="/reseller" className="flex items-center gap-2 font-bold font-[Chivo] tracking-tight" data-testid="reseller-brand">
            <span className="h-7 w-7 rounded-md bg-primary text-primary-foreground inline-flex items-center justify-center"><Handshake size={16} weight="fill" /></span>
            Fixlr Partners
          </Link>
          <nav className="hidden sm:flex items-center gap-1 ml-4">
            {NAV.map(({ to, label, icon: Icon, end }) => (
              <NavLink key={to} to={to} end={end} data-testid={`reseller-nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) => `inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors ${isActive ? "bg-primary/10 text-primary font-semibold" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`}>
                <Icon size={15} /> {label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <span className="hidden md:inline text-xs text-muted-foreground" data-testid="reseller-user-name">{user?.name}</span>
            <ThemeMenu />
            <Button variant="ghost" size="sm" onClick={logout} data-testid="reseller-logout-btn"><SignOut size={15} /></Button>
          </div>
        </div>
        <nav className="sm:hidden flex overflow-x-auto px-2 pb-2 gap-1">
          {NAV.map(({ to, label, end }) => <NavLink key={to} to={to} end={end} className={({ isActive }) => `whitespace-nowrap rounded-md px-3 py-1 text-xs ${isActive ? "bg-primary/10 text-primary font-semibold" : "text-muted-foreground"}`}>{label}</NavLink>)}
        </nav>
      </header>
      <main className="max-w-6xl mx-auto px-4 py-6">{children}</main>
    </div>
  );
}

function Tile({ label, value, sub, testId }) {
  return <div className="rounded-xl border border-border bg-card p-4" data-testid={testId}><p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{label}</p><p className="text-2xl font-bold font-[Chivo] mt-1">{value}</p>{sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}</div>;
}

function Overview({ me, reload }) {
  const [pct, setPct] = useState("");
  const [busy, setBusy] = useState(false);
  const r = me.reseller; const s = me.summary;
  const addCode = async () => {
    setBusy(true);
    try { const { data } = await API.post("/reseller-portal/codes", { discount_pct: pct }); toast.success(`${data.code} is ready to share`); setPct(""); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't create that code"); }
    setBusy(false);
  };
  return (
    <div className="space-y-6" data-testid="reseller-overview">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Welcome back</p>
        <h1 className="text-2xl font-bold font-[Chivo]">{r.name}{r.company ? <span className="text-muted-foreground font-normal"> · {r.company}</span> : null}</h1>
        <p className="text-sm text-muted-foreground mt-1">You earn <b>{r.commission_pct}%</b> of every payment your tenants make — monthly or annual — for as long as they stay subscribed.</p>
      </div>
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <Tile label="Live tenants" value={s.live_tenants} sub={`${s.tenants} signed up in total`} testId="reseller-tile-tenants" />
        <Tile label="Earned this month" value={money(s.this_month_commission)} sub="Paid after month end" testId="reseller-tile-month" />
        <Tile label="Awaiting payout" value={money(s.unpaid_commission)} sub="Including this month" testId="reseller-tile-unpaid" />
        <Tile label="Paid to you so far" value={money(s.paid_commission)} sub={`${money(s.total_commission)} earned in total`} testId="reseller-tile-paid" />
      </div>

      {me.pipeline && (me.pipeline.open > 0 || me.pipeline.won_this_month > 0) && (
        <Link to="/reseller/prospects" className={`block rounded-xl border px-4 py-3 text-sm hover:bg-muted/40 ${me.pipeline.overdue ? "border-red-300 bg-red-50/60 dark:bg-red-950/10" : me.pipeline.due_today ? "border-amber-300 bg-amber-50/60 dark:bg-amber-950/10" : "border-border bg-card"}`} data-testid="reseller-pipeline-tile">
          <span className="font-semibold"><Funnel size={14} className="inline mr-1" />{me.pipeline.open} open prospect{me.pipeline.open === 1 ? "" : "s"}</span>
          <span className="text-muted-foreground"> · {me.pipeline.due_today} due today · {me.pipeline.overdue} overdue · {me.pipeline.won_this_month} won this month</span>
        </Link>
      )}

      <section className="rounded-xl border border-border bg-card p-4 space-y-3" data-testid="reseller-codes-card">
        <div className="flex items-center gap-2"><Tag size={18} className="text-primary" /><h2 className="font-bold font-[Chivo]">Your discount codes</h2></div>
        <p className="text-xs text-muted-foreground">Give a code to a new customer. They enter it at sign-up (or on their Billing page) and get the discount for life; every payment they make earns you commission.</p>
        <div className="flex flex-wrap gap-2">
          {me.codes.length === 0 && <p className="text-sm text-muted-foreground" data-testid="reseller-codes-empty">No codes yet — create your first one below.</p>}
          {me.codes.map((c) => (
            <div key={c.id} className={`rounded-lg border px-3 py-2 ${c.active ? "border-primary/40 bg-primary/5" : "border-border opacity-60"}`} data-testid={`reseller-code-${c.code}`}>
              <div className="flex items-center gap-2"><span className="font-mono font-bold tracking-wider">{c.code}</span>
                <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => { navigator.clipboard?.writeText(c.code); toast.success("Copied"); }} data-testid={`reseller-code-copy-${c.code}`}><Copy size={13} /></button></div>
              <p className="text-[11px] text-muted-foreground">{c.discount_pct}% off for the customer · used {c.uses || 0}×{!c.active && " · switched off"}</p>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-2 pt-1">
          <div><Label className="text-xs">New code — customer discount %</Label><Input type="number" min="0" max={r.max_discount_pct} value={pct} onChange={(e) => setPct(e.target.value)} placeholder={String(r.max_discount_pct)} className="mt-1 w-32" data-testid="reseller-code-pct" /></div>
          <p className="text-xs text-muted-foreground pb-2">Up to {r.max_discount_pct}% — becomes <span className="font-mono font-semibold">{r.handle}{pct || "…"}</span></p>
          <Button size="sm" onClick={addCode} disabled={busy || pct === ""} data-testid="reseller-code-add"><Plus size={14} className="mr-1" /> Create code</Button>
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <TierCard tierInfo={me.tier_info} commissionPct={r.commission_pct} liveTenants={s.live_tenants} />
        <AlertsCard notifications={me.notifications} reload={reload} />
      </div>

      <PartnerPageCard me={me} reload={reload} />

      <LinkFunnel endpoint="/reseller-portal/links" />

      <section className="rounded-xl border border-border bg-card overflow-hidden" data-testid="reseller-recent-months">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between"><h2 className="font-bold font-[Chivo] text-sm">Recent months</h2><Link to="/reseller/statements" className="text-xs text-primary hover:underline">All statements</Link></div>
        <MonthsTable months={me.months.slice(0, 4)} canPay={false} onPdf={(p) => openPdf(`/reseller-portal/statement/${p}.pdf`)} />
      </section>

      <p className="text-xs text-muted-foreground flex items-center gap-1"><Question size={13} /> Questions about a payout? E-mail the Fixlr team — your payout details on file: <b>{r.payout_details || "not set (ask us to add them)"}</b>.</p>
    </div>
  );
}

function Tenants() {
  const [data, setData] = useState(null);
  useEffect(() => { API.get("/reseller-portal/tenants").then(({ data }) => setData(data)).catch(() => setData({ tenants: [], health_counts: {} })); }, []);
  const rows = data?.tenants ?? null;
  const failed = rows?.filter((t) => t.health?.state === "payment_failed") || [];
  return (
    <div className="space-y-4" data-testid="reseller-tenants">
      <div>
        <h1 className="text-xl font-bold font-[Chivo]">My tenants</h1>
        <HealthCounts counts={data?.health_counts} testId="reseller-health-counts" />
      </div>
      {failed.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/20 px-4 py-3 text-xs text-red-800 dark:text-red-200" data-testid="reseller-health-warning">
          <b>{failed.length === 1 ? `${failed[0].name}'s payment has failed` : `${failed.length} tenants have a failed payment`}</b> — a quick call usually fixes an expired card before the workspace locks (and keeps your commission coming).
        </div>
      )}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground"><tr><th className="text-left px-3 py-2">Tenant</th><th className="text-left px-3 py-2">Code</th><th className="text-left px-3 py-2">Signed up</th><th className="text-left px-3 py-2">Health</th><th className="text-right px-3 py-2">Payments</th><th className="text-right px-3 py-2">They've paid</th><th className="text-right px-3 py-2">Your commission</th></tr></thead>
          <tbody>
            {rows === null && <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">Loading…</td></tr>}
            {rows && rows.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground" data-testid="reseller-tenants-empty">Nobody has signed up with your code yet.</td></tr>}
            {rows?.map((t) => (
              <tr key={t.id} className={`border-t border-border ${t.health?.state === "payment_failed" ? "bg-red-50/60 dark:bg-red-950/10" : ""}`} data-testid={`reseller-tenant-${t.id}`}>
                <td className="px-3 py-2 font-medium">{t.name}<p className="text-[11px] text-muted-foreground">{t.cycle || "—"}</p></td>
                <td className="px-3 py-2 font-mono text-xs">{t.code} <span className="text-muted-foreground">−{t.discount_pct}%</span></td>
                <td className="px-3 py-2">{fmt(t.signed_up)}</td>
                <td className="px-3 py-2"><HealthBadge health={t.health} testId={`reseller-tenant-health-${t.id}`} /></td>
                <td className="px-3 py-2 text-right">{t.payments}</td>
                <td className="px-3 py-2 text-right">{money(t.paid_to_date)}</td>
                <td className="px-3 py-2 text-right font-semibold">{money(t.commission_to_date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Statements() {
  const [d, setD] = useState(null);
  useEffect(() => { API.get("/reseller-portal/commissions").then(({ data }) => setD(data)).catch(() => setD({ entries: [], months: [] })); }, []);
  if (!d) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-6" data-testid="reseller-statements">
      <div><h1 className="text-xl font-bold font-[Chivo]">Monthly statements</h1><p className="text-sm text-muted-foreground">Each month closes on the 1st. Download the PDF statement for your records; "Paid" shows the reference once Fixlr has transferred it.</p></div>
      <div className="rounded-xl border border-border bg-card overflow-hidden"><MonthsTable months={d.months} canPay={false} onPdf={(p) => openPdf(`/reseller-portal/statement/${p}.pdf`)} /></div>
      <div><h2 className="font-bold font-[Chivo] text-sm mb-2">Every commission entry</h2><div className="rounded-xl border border-border bg-card overflow-hidden"><LedgerTable entries={d.entries} /></div></div>
    </div>
  );
}

function Prospects() {
  return (
    <div className="space-y-4" data-testid="reseller-prospects">
      <div><h1 className="text-xl font-bold font-[Chivo]">Prospects</h1><p className="text-sm text-muted-foreground">The companies you're talking to — stage, next step and when. We'll nudge you here each morning about anything due, and mark a prospect won when they sign up with your code.</p></div>
      <ProspectPipeline />
    </div>
  );
}

function Resources() {
  return (
    <div className="space-y-4" data-testid="reseller-resources">
      <div><h1 className="text-xl font-bold font-[Chivo]">Resources</h1><p className="text-sm text-muted-foreground">One-pagers, narrated tours and e-mail templates — ready to forward to prospects, with your code already filled in.</p></div>
      <ResourcesHub />
    </div>
  );
}

export default function ResellerPortalPage({ tab = "overview" }) {
  const [me, setMe] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => API.get("/reseller-portal/me").then(({ data }) => setMe(data)).catch((e) => setError(e.response?.data?.detail || "Couldn't load your partner account")), []);
  useEffect(() => { load(); }, [load]);
  return (
    <Layout>
      {error ? <p className="text-sm text-red-600" data-testid="reseller-error">{error}</p>
        : !me ? <p className="text-sm text-muted-foreground">Loading…</p>
          : tab === "tenants" ? <Tenants /> : tab === "statements" ? <Statements /> : tab === "resources" ? <Resources /> : tab === "prospects" ? <Prospects /> : <Overview me={me} reload={load} />}
    </Layout>
  );
}
